const DEFAULT_OFFICIAL_DOMAIN = "gencow.com";

function stripTrailingDots(host: string): string {
  return host.replace(/\.+$/, "");
}

function isValidPortLiteral(port: string): boolean {
  if (!/^\d{1,5}$/.test(port)) return false;
  const parsed = Number(port);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 65535;
}

export function normalizePlatformProxyHost(hostHeader: string | null | undefined): string {
  const raw = (hostHeader ?? "").trim().toLowerCase();
  if (!raw) return "";

  if (raw.startsWith("[")) {
    const bracketEnd = raw.indexOf("]");
    if (bracketEnd < 0) return raw;
    const address = raw.slice(1, bracketEnd);
    const suffix = raw.slice(bracketEnd + 1);
    if (suffix && (!suffix.startsWith(":") || !isValidPortLiteral(suffix.slice(1)))) return raw;
    return address;
  }

  const colonIndex = raw.lastIndexOf(":");
  if (colonIndex > -1 && raw.indexOf(":") === colonIndex) {
    const hostname = raw.slice(0, colonIndex);
    const port = raw.slice(colonIndex + 1);
    if (isValidPortLiteral(port)) return stripTrailingDots(hostname);
  }

  return stripTrailingDots(raw);
}

export type CustomDomainResolution =
  | { kind: "direct"; appName: string; hostname: string }
  | { kind: "www_fallback_to_bare"; appName: string; hostname: string; bareHostname: string }
  | null;

export function createCustomDomainResolver(params: {
  findActiveDomainApp(host: string): Promise<string | null>;
  cacheTtlMs?: number;
  negativeCacheTtlMs?: number;
  officialDomain?: string;
}) {
  const cacheTtlMs = params.cacheTtlMs ?? 5 * 60 * 1000;
  const negativeCacheTtlMs = params.negativeCacheTtlMs ?? 60 * 1000;
  const domainCache = new Map<string, { resolution: CustomDomainResolution; expiresAt: number }>();

  return {
    async resolveCustomDomain(host: string): Promise<CustomDomainResolution> {
      const normalizedHost = normalizePlatformProxyHost(host);
      if (!normalizedHost) return null;
      const cacheResolution = (resolution: CustomDomainResolution, ttlMs: number) => {
        domainCache.set(normalizedHost, { resolution, expiresAt: Date.now() + ttlMs });
        return resolution;
      };

      const cached = domainCache.get(normalizedHost);
      if (cached && cached.expiresAt > Date.now()) return cached.resolution;

      const direct = await params.findActiveDomainApp(normalizedHost);
      if (direct) return cacheResolution({ kind: "direct", appName: direct, hostname: normalizedHost }, cacheTtlMs);

      if (normalizedHost.startsWith("www.")) {
        const bare = normalizedHost.slice(4);
        if (isOfficialDomainOrSubdomainHost(bare, params.officialDomain ?? DEFAULT_OFFICIAL_DOMAIN)) {
          return cacheResolution(null, negativeCacheTtlMs);
        }
        const bareMatch = await params.findActiveDomainApp(bare);
        if (bareMatch) {
          return cacheResolution(
            { kind: "www_fallback_to_bare", appName: bareMatch, hostname: normalizedHost, bareHostname: bare },
            cacheTtlMs,
          );
        }
      }

      return cacheResolution(null, negativeCacheTtlMs);
    },
    invalidateDomainCache(domain: string) {
      const normalizedDomain = normalizePlatformProxyHost(domain);
      if (!normalizedDomain) return;
      domainCache.delete(normalizedDomain);
      if (normalizedDomain.startsWith("www.")) {
        const bareDomain = normalizedDomain.slice(4);
        domainCache.delete(bareDomain);
        domainCache.delete(`www.${bareDomain}`);
        return;
      }
      domainCache.delete(`www.${normalizedDomain}`);
    },
  };
}

function isOfficialDomainOrSubdomainHost(host: string, officialDomain: string): boolean {
  const normalizedHost = host.toLowerCase().trim();
  const normalizedOfficialDomain = officialDomain.toLowerCase().trim();
  return normalizedHost === normalizedOfficialDomain || normalizedHost.endsWith(`.${normalizedOfficialDomain}`);
}
